import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook to fetch plan features and configuration from Firestore
 *
 * The data is stored in /config/plans and includes:
 * - Plan names and descriptions
 * - Features list for each plan (with enabled/disabled states)
 * - Project limits
 * - Most popular badge flag
 * - Display order
 *
 * @returns {Object} { plans, loading, error }
 *   - plans: { free: {...}, basic: {...}, solo: {...}, pro: {...} }
 *   - loading: boolean
 *   - error: Error object or null
 */
export function usePlans() {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const docRef = doc(db, 'config', 'plans');

    // Set up real-time listener for updates
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Remove metadata fields from the plans object
          const { updatedAt, version, ...planData } = data;
          setPlans(planData);
          setError(null);
        } else {
          // Document doesn't exist - use fallback data
          console.warn('Plans config not found in Firestore, using fallback');
          setPlans(getFallbackPlans());
          setError(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching plans config:', err);
        setError(err);
        // Use fallback on error
        setPlans(getFallbackPlans());
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { plans, loading, error };
}

/**
 * Get plans sorted by order
 * @param {Object} plans - Plans object from usePlans
 * @returns {Array} Array of plan objects sorted by order
 */
export function getSortedPlans(plans) {
  if (!plans) return [];

  return Object.entries(plans)
    .map(([key, plan]) => ({ key, ...plan }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Fallback plans data in case Firestore is unavailable
 * This matches the structure with enabled/disabled feature states
 */
function getFallbackPlans() {
  return {
    free: {
      name: "Free",
      description: "No bills :) try it out quickly to share a project",
      features: [
        { text: "1 project/website, live for 7 days", enabled: true },
        { text: "HTML hosting", enabled: true },
        { text: "Free Figmafolio domain", enabled: true },
        { text: "Analytics", enabled: false },
        { text: "Removes 'Made with Figmafolio' label", enabled: false },
        { text: "Customize Favicon", enabled: false },
        { text: "Password protection option", enabled: false }
      ],
      limits: { projects: 1 },
      mostPopular: false,
      order: 0
    },
    basic: {
      name: "Basic",
      description: "Perfect for a single, professional link",
      features: [
        { text: "1 project/website", enabled: true },
        { text: "HTML hosting", enabled: true },
        { text: "Free Figmafolio domain", enabled: true },
        { text: "Analytics", enabled: false },
        { text: "Removes 'Made with Figmafolio' label", enabled: true },
        { text: "Customize Favicon", enabled: true },
        { text: "Password protection option", enabled: true }
      ],
      limits: { projects: 1 },
      mostPopular: false,
      order: 1
    },
    solo: {
      name: "Solo",
      description: "Great for individuals & small projects",
      features: [
        { text: "3 projects/websites", enabled: true },
        { text: "HTML hosting", enabled: true },
        { text: "Connect your existing custom domains", enabled: true },
        { text: "Analytics", enabled: true },
        { text: "Removes 'Made with Figmafolio' label", enabled: true },
        { text: "Customize Favicon", enabled: true },
        { text: "Password protection option", enabled: true }
      ],
      limits: { projects: 3 },
      mostPopular: true,
      order: 2
    },
    pro: {
      name: "Pro",
      description: "For growth-focused professionals",
      features: [
        { text: "Unlimited projects/websites", enabled: true },
        { text: "HTML hosting", enabled: true },
        { text: "Connect your existing custom domains", enabled: true },
        { text: "Analytics", enabled: true },
        { text: "Removes 'Made with Figmafolio' label", enabled: true },
        { text: "Customize Favicon", enabled: true },
        { text: "Password protection option", enabled: true },
        { text: "Free portfolio review to perfect your work", enabled: true },
        { text: "Priority technical and product support", enabled: true }
      ],
      limits: { projects: -1 },
      mostPopular: false,
      order: 3
    }
  };
}

export default usePlans;
